import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const [isLoading, setIsLoading] = useState(true);

  const hasRole = (roleToCheck) => context.userRoles.includes(roleToCheck);

  const hasAnyRole = (rolesToCheck) =>
    context.userRoles.some((role) => rolesToCheck.includes(role));

  const hasAllRoles = (rolesToCheck) =>
    rolesToCheck.every((role) => context.userRoles.includes(role));

  // A real token's roles always carry Keycloak's own generic entries
  // (offline_access, uma_authorization, default-roles-<realm>) alongside
  // whichever ahedge_* role(s) actually matter — confirmed live against a
  // real interviewer-only login. A plain `roles.length === 1` check (used a
  // few places to mean "this is the user's only real role") is never true
  // for any real user because of that noise, so it silently never fires.
  // This filters those out first so "only role" checks mean what they say.
  //
  // Reads context.getUserInfo() (straight off localStorage) rather than
  // context.userRoles: useLoginMutation writes the token/user to
  // localStorage directly and navigates immediately, without ever calling
  // this context's own login()/checkAuthStatus() — so userRoles here stays
  // stale (from before login) for the render that follows a fresh sign-in.
  // Confirmed live: isOnlyRole('ahedge_interviewer') against userRoles
  // returned false right after login and only started working after a
  // subsequent full reload. getUserInfo() has no such lag.
  const isOnlyRole = (roleToCheck) => {
    const appRoles = (context.getUserInfo()?.roles || []).filter((role) =>
      role.startsWith('ahedge_')
    );
    return appRoles.length === 1 && appRoles[0] === roleToCheck;
  };

  const userRole = context.userRoles?.[0] || null;
  const isAuthenticated = !!context.user;

  useEffect(() => {
    setIsLoading(context.isLoading);
  }, [context.isLoading]);

  return {
    ...context,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isOnlyRole,
    userRole,
    isAuthenticated,
  };
};
